import type { UserRole } from '@/contexts/AuthContext';

/**
 * ========================================================================
 * SECURITY NOTICE: CLIENT-SIDE AUTHORIZATION HELPERS
 * ========================================================================
 * 
 * These functions are for UI/UX purposes ONLY - they control what users
 * see in the interface but DO NOT provide security boundaries.
 * 
 * IMPORTANT: All security enforcement MUST happen server-side via:
 * - Database RLS (Row Level Security) policies
 * - Edge function authorization checks using has_role() RPC
 * - Supabase JWT validation
 * 
 * A malicious user CAN bypass these client checks by:
 * - Modifying React state in browser DevTools
 * - Manipulating the userRole object
 * - Calling API endpoints directly
 * 
 * These checks exist purely to:
 * 1. Hide UI elements users shouldn't interact with
 * 2. Provide better UX by not showing unauthorized options
 * 3. Prevent accidental access to wrong features
 * 
 * The actual data protection is enforced by RLS and server-side checks.
 * ========================================================================
 */

/**
 * Verifica se o usuário é admin (inclui super_admin e org_admin do modelo SaaS).
 * @note UI-only check - security enforced by RLS and edge functions
 */
export const isAdmin = (userRole: UserRole | null): boolean => {
  return userRole?.role === 'admin'
    || userRole?.role === 'super_admin'
    || userRole?.role === 'org_admin';
};

/**
 * Verifica se o usuário é gerente ou admin (inclui super_admin/org_admin).
 */
export const isManager = (userRole: UserRole | null): boolean => {
  return userRole?.role === 'admin'
    || userRole?.role === 'gerente'
    || userRole?.role === 'super_admin'
    || userRole?.role === 'org_admin';
};

/**
 * Verifica se o usuário é do setor COMERCIAL (vendedor)
 */
export const isComercialUser = (userRole: UserRole | null): boolean => {
  return userRole?.setor === 'COMERCIAL';
};

/** Normalização de nome para o fallback de vendedor (espelho do servidor). */
const normalizeNome = (s: string | null | undefined): string =>
  String(s ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/['’‘`´.]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Verifica se o usuário é o VENDEDOR RESPONSÁVEL pela venda do card
 * (mesclagem de pedidos, migration 0063). Match primário por
 * userRole.gc_vendedor_id vs metadata.gc_vendedor_id; fallback por nome
 * completo normalizado. UI-only — o servidor revalida em merge-orders.ts.
 */
export const isResponsibleVendedor = (
  userRole: UserRole | null,
  job: { metadata?: { gc_vendedor_id?: string | null; gc_vendedor_nome?: string | null } | null } | null,
): boolean => {
  if (!userRole || !job) return false;
  const meta = job.metadata ?? {};
  if (userRole.gc_vendedor_id) {
    return String(meta.gc_vendedor_id ?? '') === String(userRole.gc_vendedor_id);
  }
  const callerNome = normalizeNome(userRole.nome_completo);
  return (
    callerNome.length > 0 &&
    !!meta.gc_vendedor_nome &&
    normalizeNome(meta.gc_vendedor_nome) === callerNome
  );
};

/**
 * Determina o modo de visualização do módulo Arte/Comercial
 * - 'gerente': Admin/Gerente - vê tudo
 * - 'vendedor': Setor COMERCIAL - vê quadro comercial completo
 * - 'arte': Setor ARTE - vê colunas de execução
 */
export type ArteProfileViewMode = 'gerente' | 'vendedor' | 'arte';

export const getArteProfileViewMode = (userRole: UserRole | null): ArteProfileViewMode => {
  if (isManager(userRole)) return 'gerente';
  if (isComercialUser(userRole)) return 'vendedor';
  return 'arte';
};

/**
 * Retorna o label do role para exibição
 */
export const getRoleLabel = (userRole: UserRole | null): string => {
  if (!userRole) return 'Usuário';
  if (userRole.role === 'super_admin') return 'Super Admin';
  if (userRole.role === 'org_admin') return 'Org Admin';
  if (userRole.role === 'admin') return 'Admin';
  if (userRole.role === 'gerente') return 'Gerente';
  return 'Produção';
};

/**
 * Retorna o ícone do role
 */
export const getRoleIcon = (userRole: UserRole | null): string => {
  if (!userRole) return '👤';
  if (userRole.role === 'super_admin' || userRole.role === 'org_admin') return '👑';
  if (userRole.role === 'admin') return '👤';
  if (userRole.role === 'gerente') return '👥';
  return '🔧';
};

// =====================================================
// FUNÇÕES MULTI-SETOR E MULTI-MÓDULO
// =====================================================

/**
 * Verifica se o usuário tem acesso a um setor específico
 * Admin/Gerente têm acesso a todos os setores
 */
export const hasAccessToSector = (userRole: UserRole | null, sector: string): boolean => {
  if (!userRole) return false;
  if (isManager(userRole)) return true;
  return userRole.setores?.includes(sector) ?? userRole.setor === sector;
};

/**
 * Verifica se o usuário tem múltiplos setores (para mostrar SectorSwitcher)
 * Managers não veem o SectorSwitcher pois já veem tudo
 */
export const hasMultipleSectors = (userRole: UserRole | null): boolean => {
  if (!userRole) return false;
  if (isManager(userRole)) return false;
  return (userRole.setores?.length ?? 0) > 1;
};
